import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ApiError } from "@shared/lib/api";
import { useAuth } from "@shared/lib/auth";
import { Alert, Button, Card, Input, Spinner } from "@shared/components/ui";
import { Logo } from "@shared/components/Logo";
import { LoadingScreen } from "@shared/components/LoadingScreen";
import { isCorrectAppForRole, transferSessionToApp } from "@shared/lib/roleRedirect";

export function LoginPage() {
  const auth = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const fromPath = useMemo(() => {
    const s = (location.state ?? {}) as { from?: string };
    return s.from ?? "/";
  }, [location.state]);

  const isLoggingIn = useRef(false);

  // Redirect to dashboard if already authenticated
  useEffect(() => {
    if (auth.status === "authenticated" && auth.user && !isLoggingIn.current) {
      nav(fromPath, { replace: true });
    }
  }, [auth.status, auth.user, fromPath, nav]);

  // Check for redirect message
  const messageParam = searchParams.get("message");
  const infoMessage = useMemo(() => {
    if (messageParam === "account_exists") {
      return "Your account already exists. Please sign in below.";
    }
    return null;
  }, [messageParam]);

  // Clear the message param after showing it (so refresh doesn't show it again)
  useEffect(() => {
    if (messageParam) {
      setSearchParams({}, { replace: true });
    }
  }, [messageParam, setSearchParams]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  // Show loading state while checking auth or redirecting authenticated users
  if (auth.status === "loading" || (auth.status === "authenticated" && auth.user)) {
    return <LoadingScreen />;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrors([]);
    setSubmitting(true);
    isLoggingIn.current = true;

    try {
      const result = await auth.signIn({ email, password });
      if (result?.needsSetup) {
        nav("/complete-registration", { replace: true });
      } else if (result?.requires2FA) {
        // Redirect to 2FA verification page, preserving the original destination
        // After 2FA, RequireAuth will handle role-based redirect if needed
        nav("/2fa-verify", { replace: true, state: { from: fromPath } });
      } else if (result?.userRole && !isCorrectAppForRole(result.userRole)) {
        // User logged into wrong app — transfer session to correct one
        transferSessionToApp(result.userRole);
        return;
      } else {
        nav("/welcome", { replace: true, state: { from: fromPath } });
      }
    } catch (err) {
      const errorMessages = err instanceof ApiError ? err.errors : [err instanceof Error ? err.message : "Unknown error"];
      setErrors(errorMessages.length > 0 ? errorMessages : ["Something went wrong"]);
      isLoggingIn.current = false;
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-bg">
      <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-4 py-12">
        <Logo size="lg" className="mb-8" />
        <Card title="Sign in" className="w-full">
        <form className="space-y-4" onSubmit={onSubmit}>
          {infoMessage && (
            <Alert items={[infoMessage]} variant="info" />
          )}
          <Alert items={errors} />
          <Input label="Email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
          <Input
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
          />
          <div className="text-right">
            <Link to="/forgot-password" className="text-sm text-primary hover:underline">
              Forgot password?
            </Link>
          </div>
          <div className="flex items-center justify-between gap-3">
            <Button type="submit" disabled={submitting}>
              {submitting ? "Signing in…" : "Sign in"}
            </Button>
            {submitting ? <Spinner /> : null}
          </div>
        </form>

        <div className="mt-4 text-sm text-muted">
          New here?{" "}
          <Link className="font-medium text-foreground underline" to="/signup">
            Create an account
          </Link>
        </div>
        </Card>
      </div>
    </div>
  );
}


